import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const AdditionalConditionTooltip = ({
  children,
  comment,
  disabled = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    transform: 'translate(-50%, -100%)',
  });

  const closeTimer = useRef(null);
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);

  const hasComment = typeof comment === 'string' && comment.trim().length > 0;

  const isEnabled = !disabled && hasComment;

  const updatePosition = (element) => {
    if (!element) return;

    anchorRef.current = element;

    const rect = element.getBoundingClientRect();

    const gap = 8;
    const viewportPadding = 8;

    const centerX = rect.left + rect.width / 2;

    const tooltipHeight = tooltipRef.current?.offsetHeight || 55;

    const showBelow = rect.top < tooltipHeight + gap + viewportPadding;

    const top = showBelow ? rect.bottom + gap : rect.top - gap;

    setPosition({
      top,
      left: centerX,
      transform: showBelow ? 'translateX(-50%)' : 'translate(-50%, -100%)',
    });
  };

  const handleMouseEnter = (event) => {
    if (!isEnabled) return;

    clearTimeout(closeTimer.current);

    updatePosition(event.currentTarget);

    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    if (!isEnabled) return;

    clearTimeout(closeTimer.current);

    closeTimer.current = setTimeout(() => {
      setShowTooltip(false);
    }, 100);
  };

  const handleClick = (event) => {
    if (!isEnabled) return;

    clearTimeout(closeTimer.current);

    updatePosition(event.currentTarget);

    setShowTooltip((prev) => !prev);
  };

  useLayoutEffect(() => {
    if (!showTooltip || !anchorRef.current) return;

    const element = anchorRef.current;
    const rect = element.getBoundingClientRect();

    const gap = 8;
    const viewportPadding = 8;

    const tooltipWidth = tooltipRef.current?.offsetWidth || 0;

    const tooltipHeight = tooltipRef.current?.offsetHeight || 0;

    const centerX = rect.left + rect.width / 2;

    let left = centerX;

    const halfWidth = tooltipWidth / 2;

    if (left - halfWidth < viewportPadding) {
      left = halfWidth + viewportPadding;
    }

    if (left + halfWidth > window.innerWidth - viewportPadding) {
      left = window.innerWidth - halfWidth - viewportPadding;
    }

    const showBelow = rect.top < tooltipHeight + gap + viewportPadding;

    const top = showBelow ? rect.bottom + gap : rect.top - gap;

    setPosition({
      top,
      left,
      transform: showBelow ? 'translateX(-50%)' : 'translate(-50%, -100%)',
    });
  }, [showTooltip, comment]);

  useEffect(() => {
    return () => {
      clearTimeout(closeTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!showTooltip) return;

    const closeTooltip = () => {
      setShowTooltip(false);
    };

    window.addEventListener('scroll', closeTooltip, true);

    window.addEventListener('resize', closeTooltip);

    return () => {
      window.removeEventListener('scroll', closeTooltip, true);

      window.removeEventListener('resize', closeTooltip);
    };
  }, [showTooltip]);

  if (!isEnabled) {
    return children;
  }

  return (
    <>
      {React.cloneElement(children, {
        onMouseEnter: (event) => {
          children.props?.onMouseEnter?.(event);
          handleMouseEnter(event);
        },

        onMouseLeave: (event) => {
          children.props?.onMouseLeave?.(event);
          handleMouseLeave();
        },

        onClick: (event) => {
          children.props?.onClick?.(event);
          handleClick(event);
        },

        sx: {
          ...children.props?.sx,
          cursor: 'pointer',
        },
      })}

      {showTooltip &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: 'fixed',

              top: `${position.top}px`,
              left: `${position.left}px`,

              transform: position.transform,

              width: 'max-content',
              maxWidth: '280px',

              boxSizing: 'border-box',

              padding: '5px 8px',

              backgroundColor: '#616161',
              color: '#fff',

              borderRadius: '4px',

              boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',

              zIndex: 2147483647,

              pointerEvents: 'none',

              fontFamily: 'Poppins',
              fontSize: '10px',
              fontWeight: 400,
              lineHeight: '1.2',

              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            {comment}
          </div>,
          document.body
        )}
    </>
  );
};

export default AdditionalConditionTooltip;
